import React, { useMemo, useState } from "react";
import { LayoutGrid, List, ChevronLeft, ChevronRight, Check, X } from "lucide-react";

const SEASON_BG = {
  sky: "bg-sky-200 text-sky-900", amber: "bg-amber-200 text-amber-900", rose: "bg-rose-200 text-rose-900",
  emerald: "bg-emerald-200 text-emerald-900", violet: "bg-violet-200 text-violet-900", slate: "bg-slate-200 text-slate-800",
};
const DOT = {
  sky: "bg-sky-400", amber: "bg-amber-400", rose: "bg-rose-400", emerald: "bg-emerald-400", violet: "bg-violet-400", slate: "bg-slate-400",
};
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const DOW = ["L", "M", "M", "J", "V", "S", "D"];
const md = (d) => `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const rangeMatch = (start, end, m) => (!start || !end ? false : start <= end ? m >= start && m <= end : m >= start || m <= end);

// Résolution d'une journée (miroir backend : exceptions > récurrent > référence, last-match-wins)
function seasonIdForDay(cal, year, date) {
  const m = md(date);
  const exc = (cal.exceptions || {})[String(year)] || [];
  let found = null;
  for (const r of exc) if (rangeMatch(r.start, r.end, m)) found = r.season_id;
  if (found) return found;
  for (const r of cal.recurring || []) if (rangeMatch(r.start, r.end, m)) found = r.season_id;
  if (found) return found;
  return cal.reference_season || null;
}

export default function SeasonCalendar({ seasons, calendar, onChange }) {
  const cal = calendar || { reference_season: seasons[0]?.id, recurring: [], exceptions: {} };
  const seasonsById = useMemo(() => Object.fromEntries(seasons.map((s) => [s.id, s])), [seasons]);
  const [view, setView] = useState("grid"); // grid (Vignettes) | list (Liste)
  const [year, setYear] = useState(new Date().getFullYear());
  const [applySeason, setApplySeason] = useState(seasons[0]?.id || "");
  const [anchor, setAnchor] = useState(null); // Date
  const [pending, setPending] = useState([]); // [{start:Date, end:Date}]
  const [listMonth, setListMonth] = useState(new Date().getMonth());

  const inPending = (date) => pending.some((r) => date >= r.start && date <= r.end) || (anchor && date.getTime() === anchor.getTime());

  const clickDay = (date) => {
    if (!anchor) { setAnchor(date); return; }
    const a = anchor < date ? anchor : date;
    const b = anchor < date ? date : anchor;
    setPending((p) => [...p, { start: a, end: b }]);
    setAnchor(null);
  };

  const applyPending = () => {
    if (!pending.length || !applySeason) return;
    const newRanges = pending.map((r) => ({ season_id: applySeason, start: md(r.start), end: md(r.end) }));
    onChange({ ...cal, recurring: [...(cal.recurring || []), ...newRanges] });
    setPending([]);
    setAnchor(null);
  };
  const clearPending = () => { setPending([]); setAnchor(null); };
  const setReference = (sid) => onChange({ ...cal, reference_season: sid });

  const MonthGrid = ({ month, compact }) => {
    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startDow = (first.getDay() + 6) % 7; // Lundi=0
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    const sz = compact ? "h-6 text-[10px]" : "h-10 text-sm";
    return (
      <div data-testid={`season-month-${month}`}>
        <div className={`font-semibold text-slate-700 mb-1.5 ${compact ? "text-xs" : "text-sm"}`}>{MONTHS[month]} {year}</div>
        <div className="grid grid-cols-7 gap-0.5">
          {DOW.map((d, i) => <div key={i} className={`text-center font-bold text-slate-300 ${compact ? "text-[9px]" : "text-[10px]"}`}>{d}</div>)}
          {cells.map((date, i) => {
            if (!date) return <div key={i} className={sz} />;
            const sid = seasonIdForDay(cal, year, date);
            const s = seasonsById[sid];
            const sel = inPending(date);
            return (
              <button key={i} type="button" onClick={() => clickDay(date)}
                className={`${sz} rounded flex items-center justify-center font-medium transition-all ${s ? SEASON_BG[s.color] || SEASON_BG.slate : "bg-slate-50 text-slate-400"} ${sel ? "ring-2 ring-violet-600 ring-offset-1 scale-105 z-10" : "hover:ring-1 hover:ring-violet-300"}`}
                title={s ? s.label : ""} data-testid={`season-day-${md(date)}`}>
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="season-calendar">
      {/* Barre de commandes */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid="season-view-toggle">
          <button type="button" onClick={() => setView("grid")} className={`inline-flex items-center gap-1.5 px-3 h-9 text-xs font-semibold ${view === "grid" ? "bg-violet-600 text-white" : "bg-white text-slate-600"}`} data-testid="season-view-grid"><LayoutGrid className="h-3.5 w-3.5" /> Vignettes</button>
          <button type="button" onClick={() => setView("list")} className={`inline-flex items-center gap-1.5 px-3 h-9 text-xs font-semibold ${view === "list" ? "bg-violet-600 text-white" : "bg-white text-slate-600"}`} data-testid="season-view-list"><List className="h-3.5 w-3.5" /> Liste</button>
        </div>
        <div className="inline-flex items-center gap-1">
          <button type="button" onClick={() => setYear((y) => y - 1)} className="h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50" data-testid="season-year-prev"><ChevronLeft className="h-4 w-4" /></button>
          <span className="text-sm font-bold text-slate-800 w-14 text-center" data-testid="season-year">{year}</span>
          <button type="button" onClick={() => setYear((y) => y + 1)} className="h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50" data-testid="season-year-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase font-bold text-slate-400">Appliquer</span>
          <select value={applySeason} onChange={(e) => setApplySeason(e.target.value)} className="h-9 px-2 rounded-lg border border-slate-200 bg-white text-xs font-semibold" data-testid="season-apply-select">
            {seasons.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-[10px] uppercase font-bold text-slate-400">Référence</span>
          <select value={cal.reference_season || ""} onChange={(e) => setReference(e.target.value)} className="h-9 px-2 rounded-lg border border-slate-200 bg-white text-xs font-semibold" data-testid="season-reference-select">
            {seasons.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {/* Légende */}
      <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="season-legend">
        {seasons.map((s) => (
          <span key={s.id} className="inline-flex items-center gap-1.5 text-xs text-slate-600"><span className={`h-3 w-3 rounded ${SEASON_BG[s.color] || SEASON_BG.slate}`} /> {s.label}</span>
        ))}
      </div>

      {/* Aide + sélection en cours */}
      <div className="text-[11px] text-slate-400 mb-2">
        Cliquez une 1re date puis une 2de pour sélectionner une période. Répétez pour plusieurs périodes, puis « Appliquer ». La saison de référence couvre toutes les journées non peintes. Les périodes se reconduisent automatiquement chaque année.
      </div>
      {(pending.length > 0 || anchor) && (
        <div className="flex flex-wrap items-center gap-2 mb-3 p-2.5 rounded-xl bg-violet-50 border border-violet-200" data-testid="season-pending">
          <span className="text-xs font-semibold text-violet-800">{pending.length} période(s) sélectionnée(s){anchor ? " · choisissez la date de fin…" : ""}</span>
          <button type="button" onClick={applyPending} disabled={!pending.length} className="ml-auto inline-flex items-center gap-1 px-3 h-8 rounded-lg bg-violet-600 text-white text-xs font-semibold disabled:opacity-40" data-testid="season-apply-btn"><Check className="h-3.5 w-3.5" /> Appliquer « {seasonsById[applySeason]?.label} »</button>
          <button type="button" onClick={clearPending} className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold" data-testid="season-clear-btn"><X className="h-3.5 w-3.5" /> Annuler</button>
        </div>
      )}

      {view === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="season-grid-view">
          {MONTHS.map((_, m) => <div key={m} className="rounded-xl border border-slate-200 bg-white p-3"><MonthGrid month={m} compact /></div>)}
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-4 max-w-md mx-auto" data-testid="season-list-view">
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={() => setListMonth((m) => (m === 0 ? (setYear((y) => y - 1), 11) : m - 1))} className="h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center" data-testid="season-list-prev"><ChevronLeft className="h-4 w-4" /></button>
            <div className="flex items-center gap-2">
              <select value={listMonth} onChange={(e) => setListMonth(Number(e.target.value))} className="h-9 px-2 rounded-lg border border-slate-200 text-sm font-semibold" data-testid="season-list-month">
                {MONTHS.map((mn, i) => <option key={i} value={i}>{mn}</option>)}
              </select>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-9 px-2 rounded-lg border border-slate-200 text-sm font-semibold" data-testid="season-list-year">
                {Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 1 + i).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button type="button" onClick={() => setListMonth((m) => (m === 11 ? (setYear((y) => y + 1), 0) : m + 1))} className="h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center" data-testid="season-list-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <MonthGrid month={listMonth} />
        </div>
      )}
    </div>
  );
}
